import { Checkbox, Hint, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import SettingsLayout from 'modules/profile/components/settings-layout'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import { updateSalesFunnelSettings } from 'modules/profile/sales-funnels-settings/api/sales-funnels-settings-api'
import SalesFunnelsSettingsSkeleton from 'modules/profile/sales-funnels-settings/components/sales-funnels-settings-skeleton'
import { SalesFunnelsSettingsLoco } from 'modules/profile/sales-funnels-settings/constants/sales-funnels-settings-loco'
import { useSalesFunnelsSettings } from 'modules/profile/sales-funnels-settings/hooks/use-sales-funnels-settings'
import {
  SalesFunnelsSettingsErrorInterface,
  SalesFunnelsSettingsInterface,
} from 'modules/profile/sales-funnels-settings/types/sales-funnels-settings-interface'
import { NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import CodeEditor from 'shared/components/code-editor'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const defaultValues: SalesFunnelsSettingsInterface = {
  trackingCode: null,
  storeUtmData: false,
}

const defaultErrors = {
  trackingCode: '',
  storeUtmData: '',
  common: [],
}

const SalesFunnelsSettings: NextPage = () => {
  const { t } = useLocoTranslation()

  const [tempState, setTempState] = useState<SalesFunnelsSettingsInterface>(defaultValues)

  const [errors, setErrors] = useState<SalesFunnelsSettingsErrorInterface>(defaultErrors)

  const [isFetching, setIsFetching] = useState(false)

  const { salesFunnelData, mutate } = useSalesFunnelsSettings()
  const onSubmit = async () => {
    try {
      setErrors(defaultErrors)
      setIsFetching(true)
      const response = await updateSalesFunnelSettings(tempState)
      await mutate(response.data, false)
      setIsFetching(false)
      toast.success(t('global.changes_saved'))
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const salesFunnelsSettingErrors = e.errors
        setErrors(prev => ({
          ...prev,
          trackingCode: salesFunnelsSettingErrors.fields.trackingCode?.join(''),
        }))
        if (e.errors.common) {
          const commonErrors = e.errors.common
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors,
            }
          })
        }
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  useEffect(() => {
    if (salesFunnelData) {
      setTempState(salesFunnelData)
    }
  }, [salesFunnelData])

  const handleChangeTrackingCode = (value: string) => {
    setTempState({ ...tempState, trackingCode: value })
  }

  const [isCodeValid, setIsCodeValid] = useState(true)

  return (
    <SettingsLayout
      titles={[{ caption: t(settingsMenuLocoKeys.sales_funnel), isStatic: true }]}
      onSave={onSubmit}
      disabled={!salesFunnelData || !isCodeValid}
      isFetching={isFetching}
    >
      {salesFunnelData ? (
        <div className="bg-background-secondary flex h-fit w-full flex-col gap-7 overflow-x-hidden rounded-md p-5 lg:gap-10 [&>*]:flex-1">
          <div className="flex flex-col gap-3">
            <Checkbox
              label={t('dashboard.settings.sales_funnels_settings.store_UTM')}
              value={tempState.storeUtmData}
              disabled={isFetching}
              onChange={value => setTempState({ ...tempState, storeUtmData: value })}
            />
            <CodeEditor
              label={
                <Hint
                  label={t('dashboard.settings.sales_funnels_settings.tracking_code_popover_title')}
                >
                  {t('dashboard.settings.sales_funnels_settings.tracking_code_title')}
                </Hint>
              }
              value={tempState.trackingCode}
              onChange={handleChangeTrackingCode}
              setIsValid={setIsCodeValid}
            />
          </div>
          {errors.common && (
            <>
              {errors.common.map((error, index) => (
                <p className="text-danger px-6 text-sm" key={index}>
                  {error}
                </p>
              ))}
            </>
          )}
        </div>
      ) : (
        <SalesFunnelsSettingsSkeleton />
      )}
    </SettingsLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  SalesFunnelsSettingsLoco,
  'global.settings',
)

export default SalesFunnelsSettings
